import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { colorDeLaSituacion } from './estadosDeFactura';
import type { EnLaCola } from './tiposDelProcesoSri';

export function TablaDeLaCola({
  filas,
  conMensaje = false,
  accion,
  enCurso,
  iconoVacio,
  mensajeVacio,
  entidad,
}: {
  filas: EnLaCola[];
  conMensaje?: boolean;
  accion: {
    rotulo: string;
    icono: LucideIcon;
    alPulsar: (id: number) => void;
    disponible: boolean;
    aviso?: string;
  };
  enCurso: number | null;
  iconoVacio: LucideIcon;
  mensajeVacio: string;
  entidad: string;
}) {
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);

  const totalPaginas = Math.max(1, Math.ceil(filas.length / tamanio));
  const actual = Math.min(pagina, totalPaginas - 1);

  const datos = {
    contenido: filas.slice(actual * tamanio, actual * tamanio + tamanio),
    totalElementos: filas.length,
    pagina: actual,
    tamanio,
    totalPaginas,
  };

  const columnas: Columna<EnLaCola>[] = [
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      contenido: (f) => (
        <span className="block">
          <span className="block truncate font-medium text-armazon-700">{f.cliente}</span>
          <span className="block font-mono text-[10px] text-armazon-400">{f.identificacion}</span>
        </span>
      ),
    },
    { clave: 'periodo', encabezado: 'Período', contenido: (f) => f.periodo },
    {
      clave: 'numero',
      encabezado: 'Nº factura',
      contenido: (f) => (
        <span className="font-mono text-xs">
          {f.numero ?? <span className="text-armazon-400">SIN EMITIR</span>}
        </span>
      ),
    },
    {
      clave: 'fechaEmision',
      encabezado: 'Emitida',
      contenido: (f) => (
        <span className="whitespace-nowrap text-[11px] text-armazon-500">
          {enFecha(f.fechaEmision)}
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => <span className="importe">${f.total.toFixed(2)}</span>,
    },
    {
      clave: 'situacion',
      encabezado: 'Estado SRI',
      alinear: 'centro',
      contenido: (f) => (
        <span className={`text-[11px] font-semibold uppercase ${colorDeLaSituacion(f.situacion)}`}>
          {f.situacion}
        </span>
      ),
    },
    ...(conMensaje
      ? [
          {
            clave: 'mensaje',
            encabezado: 'Qué dijo el SRI',

            contenido: (f: EnLaCola) => (
              <span className="block max-w-md text-[11px] leading-relaxed text-red-700">
                {f.mensaje ?? '—'}
              </span>
            ),
          },
          {
            clave: 'intentos',
            encabezado: 'Intentos',
            alinear: 'centro' as const,
            contenido: (f: EnLaCola) => (
              <span
                className={`text-xs tabular-nums ${f.intentos >= 5 ? 'font-bold text-red-700' : 'text-armazon-500'}`}
              >
                {f.intentos}
              </span>
            ),
          },
        ]
      : []),
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'derecha',
      contenido: (f) => (
        <span className="flex justify-end">
          <BotonDeFila
            icono={accion.icono}
            titulo={accion.disponible ? accion.rotulo : (accion.aviso ?? accion.rotulo)}
            alPulsar={() => accion.alPulsar(f.id)}
            ocupado={enCurso === f.id || !accion.disponible}
          />
        </span>
      ),
    },
  ];

  return (
    <Tabla
      datos={datos}
      cargando={false}
      columnas={columnas}
      tamanio={tamanio}
      alCambiarPagina={setPagina}
      alCambiarTamanio={(nuevo) => {
        setTamanio(nuevo);
        setPagina(0);
      }}
      iconoVacio={iconoVacio}
      mensajeVacio={mensajeVacio}
      entidad={entidad}
    />
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return dia && mes && anio ? `${dia}/${mes}/${anio}` : iso;
}
